import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import { 
  ArrowRightIcon, 
  ClockIcon, 
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

var userEmail;
var JSToken;
var accessToken;
if (localStorage.user) {
  userEmail = localStorage.user_email;
  JSToken = JSON.parse(localStorage.user);
  accessToken = JSToken.access_token;
}
const config = {
  headers: { Authorization: `Bearer ${accessToken}` },
};

const CurrentRequestCard = ({ requestId, deduction }) => {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `https://dash.bestdiplomats.org/api/method/get_current_transfer_request_details?request_id=${requestId}`,
          config
        );
        if (response.status===200) {
          setRequest(response.data.data[0].data);
              console.log(response.data.data[0].dataction)

        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (requestId) {
      fetchRequest();
    }
  }, [requestId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <Skeleton height={30} width={250} className="mb-4" />
        <Skeleton count={5} height={80} className="mb-3" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-red-600">Error loading request: {error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Current Transfer Request</h3>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(request?.status)}`}>
            {request?.status}
          </span>
        </div>
        <p className="text-indigo-100 text-sm mt-1">Request ID: {request?.request_id}</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Submission Info */}
        <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
          <ClockIcon className="h-6 w-6 text-gray-500" />
          <div>
            <p className="text-xs text-gray-500 font-medium">Submitted On</p>
            <p className="text-sm font-semibold text-gray-900">
              {formatDateTime(request?.submission_date)}
            </p>
          </div>
        </div>

        {/* Event Transfer Flow */}
        <div className="relative">
          <div className="flex items-center justify-between gap-4">
            {/* Source Event */}
            <div className="flex-1 bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">FROM</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{request?.source_event?.title}</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <CalendarIcon className="h-4 w-4" />
                  {formatDate(request?.source_event?.start_date)}
                </div>
                {request?.source_event?.location && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPinIcon className="h-4 w-4" />
                    {request?.source_event?.location}
                  </div>
                )}
                <div className={`flex items-center gap-2 font-semibold mt-2 ${
                  request?.source_event?.days_remaining < 30 ? 'text-red-600' : 'text-green-600'
                }`}>
                  <ClockIcon className="h-4 w-4" />
                  {request?.source_event?.days_remaining} days remaining
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex-shrink-0">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-3 rounded-full">
                <ArrowRightIcon className="h-6 w-6 text-white" />
              </div>
            </div>

            {/* Destination Event */}
            <div className="flex-1 bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-purple-600 bg-purple-100 px-2 py-1 rounded">TO</span>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{request?.destination_event?.title}</h4>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <CalendarIcon className="h-4 w-4" />
                  {formatDate(request?.destination_event?.start_date)}
                </div>
                {request?.destination_event?.location && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPinIcon className="h-4 w-4" />
                    {request?.destination_event?.location}
                  </div>
                )}
                <div className={`flex items-center gap-2 font-semibold mt-2 ${
                  request?.destination_event?.days_remaining < 30 ? 'text-red-600' : 'text-green-600'
                }`}>
                  <ClockIcon className="h-4 w-4" />
                  {request?.destination_event?.days_remaining} days remaining
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deduction Info */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-800">Deduction Applied</p>
              <p className="text-sm text-yellow-700 mt-1">
                {deduction} - Requested {request?.days_before_event_requested} days before event
              </p>
            </div>
          </div>
        </div>

        {/* Supporting Documents */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5" />
            Supporting Documents
          </h4>
          <div className="grid grid-cols-3 gap-3">
            {/* First Receipt */}
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium mb-2">First Receipt</p>
              {request?.documents?.first_receipt ? (
                <a
                  href={`https://dash.bestdiplomats.org${request.documents.first_receipt}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-blue-500 hover:bg-blue-600 text-white text-sm py-2 px-3 rounded transition-colors"
                >
                  View
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <span className="inline-flex items-center justify-center w-full bg-red-100 text-red-800 text-xs py-2 px-3 rounded font-semibold">
                  Missing
                </span>
              )}
            </div>

            {/* Second Receipt */}
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium mb-2">Second Receipt</p>
              {request?.documents?.second_receipt ? (
                <a
                  href={`https://dash.bestdiplomats.org${request.documents.second_receipt}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-blue-500 hover:bg-blue-600 text-white text-sm py-2 px-3 rounded transition-colors"
                >
                  View
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <span className="inline-flex items-center justify-center w-full bg-red-100 text-red-800 text-xs py-2 px-3 rounded font-semibold">
                  Missing
                </span>
              )}
            </div>

            {/* Appeal Attachment */}
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500 font-medium mb-2">Appeal Attachment</p>
              {request?.documents?.appeal_attachment ? (
                <a
                  href={`https://dash.bestdiplomats.org${request.documents.appeal_attachment}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-blue-500 hover:bg-blue-600 text-white text-sm py-2 px-3 rounded transition-colors"
                >
                  View
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <span className="inline-flex items-center justify-center w-full bg-gray-100 text-gray-600 text-xs py-2 px-3 rounded font-semibold">
                  N/A
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Appeal Reason */}
        {request?.appeal_reason && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">Appeal Reason</h4>
            <p className="text-sm text-purple-800">{request.appeal_reason}</p>
          </div>
        )}

        {/* Admin Comments */}
        {(request?.approval_comment || request?.rejection_comment) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Admin Comments</h4>
            {request?.approval_comment && (
              <div className="mb-2">
                <p className="text-xs text-green-600 font-medium">Approval Comment:</p>
                <p className="text-sm text-gray-700">{request.approval_comment}</p>
              </div>
            )}
            {request?.rejection_comment && (
              <div>
                <p className="text-xs text-red-600 font-medium">Rejection Comment:</p>
                <p className="text-sm text-gray-700">{request.rejection_comment}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentRequestCard;